import { type ReactNode, useMemo } from 'react';

import {
	AssetsIcon,
	AtlassianIcon,
	BitbucketIcon,
	CompassIcon,
	ConfluenceIcon,
	FocusIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	TalentIcon,
	TrelloIcon,
} from '@atlaskit/logo';

import { ThirdPartyConfigs } from './3p-product-configs';
import { get3PLogo, type RovoLogo, RovoLogoFromLogo } from './icons';
import { AssetsSearchResultIcon } from './icons/assets';
import { AtlasSearchResultIcon } from './icons/atlas-search';
import { CompassSearchResultIcon } from './icons/compass';
import { ConfluenceSearchResultIcon } from './icons/confluence-search';
import { FocusSearchResultIcon } from './icons/focus';
import { JiraProductDiscoverySearchResultIcon } from './icons/jira-product-discovery';
import { JiraSearchResultIcon } from './icons/jira-search';
import { JiraServiceManagementSearchResultIcon } from './icons/jira-service-management';
import { TalentSearchResultIcon } from './icons/talent';
import { TrelloSearchResultIcon } from './icons/trello';
// Pure product key constants imported from product-keys.ts (source of truth).
// Re-exported below so existing consumers continue to work.
import {
	ProductKeys,
	ProductKeys1PForAnalytics,
	ProductKeys3P,
	PrimaryProductKeys,
	PRODUCTS_ORDER,
	PRODUCTS_ORDER_WITH_3P_BY_POPULARITY,
	ProductsOrder3P,
	all1pProductKeys,
	all3pProductKeys,
	allProductKeys,
	is1pProductKey,
	is3pProductKey,
	isProductKey,
	productsSupportingEmptyQuery,
	type PrimaryProductKey,
	type ProductKeys1P,
} from './product-keys';

export interface ProductConfig {
	displayName: ReactNode;
	Icon: RovoLogo;
	IconBorderless?: RovoLogo;
	is3P?: boolean;
	SearchResultIcon?: RovoLogo;
}

// Re-export pure product key constants from product-keys.ts (source of truth).
// These re-exports maintain backward compatibility for 298 consumers of this file.
// TODO: Migrate pure-data consumers to import from product-keys.ts directly (Part 4),
// then remove these re-exports and this eslint-disable.
/* eslint-disable no-barrel-files/no-barrel-files */
export {
	ProductKeys,
	ProductKeys3P,
	PrimaryProductKeys,
	PRODUCTS_ORDER,
	PRODUCTS_ORDER_WITH_3P_BY_POPULARITY,
	allProductKeys,
	all3pProductKeys,
	all1pProductKeys,
	is1pProductKey,
	is3pProductKey,
	isProductKey,
	productsSupportingEmptyQuery,
	ProductsOrder3P,
	ProductKeys1PForAnalytics,
	type ProductKeys1P,
	type PrimaryProductKey,
};
/* eslint-enable no-barrel-files/no-barrel-files */

/*
 * The logic here is that if a product key is not in the 1P keys, it must be a 3P key.
 * We can't use `is3pProductKey` as it will impact how we are handling connectors.
 * This is meant to only be used for analytics purposes, to differentiate between 1P and 3P products.
 */
export const is3pProductKeyForAnalytics: (
	key: string | undefined,
) => key is ProductKeys1PForAnalytics = (
	key: string | undefined,
): key is ProductKeys1PForAnalytics =>
	!Object.values(ProductKeys1PForAnalytics).includes(key as ProductKeys1PForAnalytics);

export const getUppercasePrimaryProductKey: (
	productKey: PrimaryProductKey | undefined,
) => Uppercase<PrimaryProductKey> | undefined = (
	productKey: PrimaryProductKey | undefined,
): Uppercase<PrimaryProductKey> | undefined =>
	productKey ? (productKey.toUpperCase() as Uppercase<PrimaryProductKey>) : undefined;

/**
 * This is the source of truth for the products that are supported in Full Page Search.
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * For 3P products please see ./3p-product-configs
 */
export const products1P: Record<ProductKeys1P, ProductConfig> = {
	[ProductKeys.Confluence]: {
		displayName: 'Confluence',
		Icon: RovoLogoFromLogo(ConfluenceIcon, 'Confluence'),
		SearchResultIcon: get3PLogo(ConfluenceSearchResultIcon, 'Confluence', 'large'),
	},
	[ProductKeys.Atlas]: {
		displayName: 'Home',
		Icon: RovoLogoFromLogo(AtlassianIcon, 'Home'),
		SearchResultIcon: get3PLogo(AtlasSearchResultIcon, 'Home', 'large'),
	},
	[ProductKeys.Jira]: {
		displayName: 'Jira',
		Icon: RovoLogoFromLogo(JiraIcon, 'Jira'),
		SearchResultIcon: get3PLogo(JiraSearchResultIcon, 'Jira', 'large'),
	},
	[ProductKeys.JiraProductDiscovery]: {
		displayName: 'Jira Product Discovery',
		Icon: RovoLogoFromLogo(JiraProductDiscoveryIcon, 'Jira Product Discovery'),
		SearchResultIcon: get3PLogo(
			JiraProductDiscoverySearchResultIcon,
			'Jira Product Discovery',
			'large',
		),
	},
	[ProductKeys.JiraServiceManagement]: {
		displayName: 'Jira Service Management',
		Icon: RovoLogoFromLogo(JiraServiceManagementIcon, 'Jira Service Management'),
		SearchResultIcon: get3PLogo(
			JiraServiceManagementSearchResultIcon,
			'Jira Service Management',
			'large',
		),
	},
	[ProductKeys.Bitbucket]: {
		displayName: 'Bitbucket',
		Icon: RovoLogoFromLogo(BitbucketIcon, 'Bitbucket'),
		SearchResultIcon: RovoLogoFromLogo(BitbucketIcon, 'large'),
	},
	[ProductKeys.Trello]: {
		displayName: 'Trello',
		Icon: RovoLogoFromLogo(TrelloIcon, 'Trello'),
		SearchResultIcon: get3PLogo(TrelloSearchResultIcon, 'Trello', 'large'),
	},
	[ProductKeys.Compass]: {
		displayName: 'Compass',
		Icon: RovoLogoFromLogo(CompassIcon, 'Compass'),
		SearchResultIcon: get3PLogo(CompassSearchResultIcon, 'Compass', 'large'),
	},
	[ProductKeys.Focus]: {
		displayName: 'Focus',
		Icon: RovoLogoFromLogo(FocusIcon, 'Focus'),
		SearchResultIcon: get3PLogo(FocusSearchResultIcon, 'Focus', 'large'),
	},
	[ProductKeys.Assets]: {
		displayName: 'Assets',
		Icon: RovoLogoFromLogo(AssetsIcon, 'Assets'),
		SearchResultIcon: get3PLogo(AssetsSearchResultIcon, 'Assets', 'large'),
	},
	[ProductKeys.Talent]: {
		displayName: 'Talent',
		Icon: RovoLogoFromLogo(TalentIcon, 'Talent'),
		SearchResultIcon: get3PLogo(TalentSearchResultIcon, 'Talent', 'large'),
	},
};

const getProducts3PFromConfig = (): Record<ProductKeys3P, ProductConfig> => {
	// The use of Partial, the if (config) and the cast here are intentional TS gymnastics
	// We KNOW that this returns a config for all the ProductKeys3P, so we cast that
	// The TS compiler can't know that, and we'd be forced to write UI code like:
	//
	// const Icon = configs[productKey].Icon
	// return Icon && <Icon />
	//
	// Because TS can't prove productKey can be guaranteed to index into configs. But we know it can!
	const thirdPartyProducts = ThirdPartyConfigs;
	const configs: Partial<Record<ProductKeys3P, ProductConfig>> = {};

	all3pProductKeys.map((productKey3P) => {
		const config = thirdPartyProducts.get(productKey3P);
		if (config) {
			configs[productKey3P] = {
				displayName: config.displayName,
				Icon: config.icon,
				IconBorderless: config.iconBorderless,
				is3P: true,
				...(config.searchResultIcon && { SearchResultIcon: config.searchResultIcon }),
			};
		}
	});

	return configs as Record<ProductKeys3P, ProductConfig>;
};

/**
 * This is the source of truth for the products that are supported in Full Page Search for use by the UI
 * Note: there is additional logic in controllers/store/bootstrap/utils.ts for products being rolled out.
 *
 * If you just need the set of productKeys, please import { allProductKeys } or other similar helpers
 *
 * To get the list of products available to a user (a state concern) please see the exports in store/search
 */
export const useProductConfigs: () => Record<ProductKeys, ProductConfig> = (): Record<
	ProductKeys,
	ProductConfig
> => {
	return useMemo((): Record<ProductKeys, ProductConfig> => {
		const thirdPartyProducts = getProducts3PFromConfig();

		return {
			...products1P,
			...thirdPartyProducts,
		};
	}, []);
};

export const useProductConfig: (productKey: ProductKeys) => ProductConfig = (
	productKey: ProductKeys,
): ProductConfig => {
	return useProductConfigs()[productKey];
};

// Used by SAIN to detrermine knowledge source filtering
enum KnowledgeSourceTypeValues {
	GITHUB = 'github',
	CONFLUENCE = 'confluence',
	JIRA = 'jira',
	ATLAS = 'atlas',
	HELP_AGGREGATOR = 'help_aggregator',
	GOOGLE_DRIVE = 'google_drive',
	MICROSOFT_SHAREPOINT = 'microsoft_sharepoint',
	SLACK = 'slack',
	MICROSOFT_TEAMS = 'teams',
	FIGMA = 'figma',
	CONFLUENCE_DC = 'confluence-dc',
	ATLASSIAN_WEB_CRAWLER = 'atlassian-web-crawler',
	LOOM = 'loom',
	GOOGLE_CALENDAR = 'google_calendar',
	ASSETS = 'cmdb',
	SALESFORCE = 'salesforce',
	ZENDESK = 'zendesk',
	SERVICENOW = 'servicenow',
}

export const MapProductToKnowledgeSources: { [key: string]: string } = {
	[ProductKeys.Confluence]: KnowledgeSourceTypeValues.CONFLUENCE,
	[ProductKeys.Github]: KnowledgeSourceTypeValues.GITHUB,
	[ProductKeys.Jira]: KnowledgeSourceTypeValues.JIRA,
	[ProductKeys.Atlas]: KnowledgeSourceTypeValues.ATLAS,
	[ProductKeys.GoogleDrive]: KnowledgeSourceTypeValues.GOOGLE_DRIVE,
	[ProductKeys.Sharepoint]: KnowledgeSourceTypeValues.MICROSOFT_SHAREPOINT,
	[ProductKeys.Slack]: KnowledgeSourceTypeValues.SLACK,
	[ProductKeys.Teams]: KnowledgeSourceTypeValues.MICROSOFT_TEAMS,
	[ProductKeys.Figma]: KnowledgeSourceTypeValues.FIGMA,
	[ProductKeys.ConfluenceDC]: KnowledgeSourceTypeValues.CONFLUENCE_DC,
	[ProductKeys.AtlassianWebCrawler]: KnowledgeSourceTypeValues.ATLASSIAN_WEB_CRAWLER,
	[ProductKeys.Loom]: KnowledgeSourceTypeValues.LOOM,
	[ProductKeys.GoogleCalendar]: KnowledgeSourceTypeValues.GOOGLE_CALENDAR,
	[ProductKeys.Assets]: KnowledgeSourceTypeValues.ASSETS,
	[ProductKeys.Salesforce]: KnowledgeSourceTypeValues.SALESFORCE,
	[ProductKeys.Zendesk]: KnowledgeSourceTypeValues.ZENDESK,
	[ProductKeys.ServiceNow]: KnowledgeSourceTypeValues.SERVICENOW,
};
